<template>
    <div class="class-1">
        <!-- Header -->
        <AppHeader />

        <!-- Create Floor Form -->
        <div class="container mt-4">
            <div class="row">
                <div class="col-12">
                    <h2>Create New Floor</h2>
                    <form @submit.prevent="handleCreateFloor">
                        <!-- Floor Name Input -->
                        <div class="form-row">
                            <div class="form-group col-md-12">
                                <label for="floor">Floor Name <span class="required">*</span></label>
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    id="floor" 
                                    v-model="form.floor" 
                                    placeholder="Enter Floor Name (e.g., Ground Floor, First Floor)" 
                                    required
                                    maxlength="50"
                                >
                                <small class="">Maximum 50 characters</small>
                            </div>
                        </div>

                        <!-- Branch Selection -->
                        <div class="form-row">
                            <div class="form-group col-md-12">
                                <label for="branchId">Select Branch</label>
                                <select 
                                    class="form-control" 
                                    id="branchId" 
                                    v-model="form.branchId"
                                >
                                    <option value="">-- Select Branch (Optional) --</option>
                                    <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                                        {{ branch.name }}
                                    </option>
                                </select>
                                <small class="">Select a branch to associate this floor with</small>
                            </div>
                        </div>

                        <!-- Buttons -->
                        <div class="form-row">
                            <div class="form-group col-12 d-flex justify-content-between">
                                <button type="button" class="btn btn-secondary" @click="cancel">
                                    Cancel
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="submitting">
                                    {{ submitting ? 'Creating...' : 'Create Floor' }}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            form: {
                floor: '',
                branchId: '',
            },
            branches: [],
            submitting: false,
            currentDate: '',
            currentTime: '',
        };
    },
    mounted() {
        this.updateDateTime();
        this.startDateTimeUpdate();
        this.fetchBranches();
    },
    methods: {
        updateDateTime() {
            const now = new Date();
            this.currentDate = now.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
            this.currentTime = now.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
        },

        startDateTimeUpdate() {
            setInterval(() => {
                this.updateDateTime();
            }, 1000);
        },

        async fetchBranches() {
            try {
                const token = localStorage.getItem('authToken');
                const appId = localStorage.getItem('appId') || 1;
                
                const response = await fetch(`${this.$BASE_API_URL}/api/branches?appId=${appId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch branches');
                }

                const data = await response.json();
                this.branches = data.data;
            } catch (error) {
                console.error('Error fetching branches:', error);
                this.$toast.error('Failed to load branches');
            }
        },

        async handleCreateFloor() {
            if (!this.form.floor.trim()) {
                this.$toast.error('Floor name is required');
                return;
            }

            this.submitting = true;
            
            try {
                this.$root.isLoading = true;
                const token = localStorage.getItem('authToken');
                const appId = localStorage.getItem('appId') || 1;

                const payload = {
                    appId: parseInt(appId),
                    floor: this.form.floor.trim(),
                };

                // Only include branchId if it's selected
                if (this.form.branchId) {
                    payload.branchId = parseInt(this.form.branchId);
                }

                const response = await fetch(`${this.$BASE_API_URL}/api/floors`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });

                const data = await response.json();

                if (response.ok) {
                    this.$toast.success('Floor created successfully!');
                    this.$router.push('/floors');
                } else {
                    this.$toast.error(data.message || 'Failed to create floor');
                    
                    // Handle validation errors
                    if (data.errors) {
                        Object.values(data.errors).forEach(error => {
                            this.$toast.error(error[0]);
                        });
                    }
                }
            } catch (error) {
                console.error('Error:', error);
                this.$toast.error('An error occurred. Please try again.');
            } finally {
                this.$root.isLoading = false;
                this.submitting = false;
            }
        },

        cancel() {
            this.$router.go(-1);
        },
    },
};
</script>

<style scoped>
.class-1 {
    font-family: 'Poppins', sans-serif;
}

.container {
    max-width: 600px;
    margin: 0 auto;
    padding: 20px;
}

h2 {
    margin-bottom: 30px;
    font-size: 24px;
}

.form-group {
    margin-bottom: 20px;
}

label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
}

.required {
    color: #ff0000;
}

.form-control {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    transition: border-color 0.3s;
}

.form-control:focus {
    outline: none;
    border-color: #4caf50;
    box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.text-muted {
    font-size: 12px;
    margin-top: 5px;
    display: block;
}

.btn {
    padding: 10px 30px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s;
}

.btn-primary {
    background-color: #4caf50;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background-color: #45a049;
}

.btn-primary:disabled {
    background-color: #a5d6a7;
    cursor: not-allowed;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background-color: #5a6268;
}

.d-flex {
    display: flex;
}

.justify-content-between {
    justify-content: space-between;
}

.mt-4 {
    margin-top: 2rem;
}

select.form-control {
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 15px;
    padding-right: 35px;
}
</style>